import { Field, Float, Int, ObjectType } from '@nestjs/graphql'
import { BaseModelDto } from '../base/baseModel.dto'
import { ownerOrStaffOnly } from '../base/ownerOrStaffOnly.middleware'
import { staffOnly } from '../base/staffOnly.middleware'
import { BookCategoryFitDto, BookDto } from '../book'
import { ArcTeamDto } from '../arc-team'
import { ArcClaimDto } from './claim'
import { bsConstants } from '../../module.constants'
import { ArcModelStatsDto } from './modelStats.dto'

@ObjectType()
export class ArcDto extends BaseModelDto {
  @Field(type => BookDto, { nullable: true })
  book?: BookDto

  @Field(t => Int, { nullable: true })
  bookId?: number

  @Field(t => Int, { nullable: true })
  status?: number

  @Field(type => ArcTeamDto, { nullable: true })
  arcTeam?: ArcTeamDto

  @Field(t => Int, { nullable: true })
  arcTeamId?: number

  @Field(t => Boolean, { nullable: true })
  canClaim?: boolean

  /** Whether the requesting reader has saved/shortlisted this ARC (sc-26587). */
  @Field(t => Boolean, { nullable: true })
  isSaved?: boolean

  /** Number of accounts currently saving this ARC — author "Saves" stat (sc-26587). */
  @Field(t => Int, { nullable: true })
  saveCount?: number

  @Field(type => String, { nullable: true })
  specialInformation?: string

  @Field(type => Int, { defaultValue: bsConstants.ARCS.TYPE.EBOOK })
  arcType: number = bsConstants.ARCS.TYPE.EBOOK

  @Field(type => Int, { defaultValue: bsConstants.ARCS.DISCOVERABLE_TYPE.PUBLIC })
  discoverableType: number = bsConstants.ARCS.DISCOVERABLE_TYPE.PUBLIC

  @Field(type => Int, { defaultValue: bsConstants.ARCS.PARTICIPATION_TYPE.PUBLIC })
  participationType: number = bsConstants.ARCS.PARTICIPATION_TYPE.PUBLIC

  @Field(type => Int, { defaultValue: bsConstants.ARCS.PARTICIPATION_PREVENTION_TYPE.OUTSTANDING })
  participationPreventionType: number = bsConstants.ARCS.PARTICIPATION_PREVENTION_TYPE.OUTSTANDING

  @Field(type => Int, { defaultValue: bsConstants.ARCS.SITE_WIDE_PARTICIPATION_PREVENTION_TYPE.NONE })
  siteWideParticipationPreventionType: number = bsConstants.ARCS.SITE_WIDE_PARTICIPATION_PREVENTION_TYPE.NONE

  @Field(type => Int, { defaultValue: bsConstants.ARCS.COPY_COUNT_TYPE.ALL })
  copyCountType: number = bsConstants.ARCS.COPY_COUNT_TYPE.ALL

  @Field(type => Int, { nullable: true })
  copyCount?: number

  @Field(type => Int, { defaultValue: bsConstants.ARCS.SERIES_LIMIT_TYPE.ANYONE })
  seriesLimitType: number = bsConstants.ARCS.SERIES_LIMIT_TYPE.ANYONE

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  reviewFlags: number = 0

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  optionalReviewFlags: number = 0

  @Field(type => Int, { nullable: true, defaultValue: 1 })
  requiredReviewCount: number = 1

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  optionalReviewCount: number = 0

  /** Server-populated calculated values (e.g. when Apple Books has no codes). Default 0 when not set. */
  @Field(type => Int, { nullable: true, defaultValue: 0 })
  calculatedReviewFlags?: number

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  calculatedOptionalReviewFlags?: number

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  calculatedRequiredReviewCount?: number

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  calculatedOptionalReviewCount?: number

  @Field(type => Int, { nullable: true, defaultValue: 0 })
  calculatedRequiredSitesCount?: number

  @Field({ nullable: true })
  liveDate?: Date

  @Field(type => Int, { defaultValue: 0 })
  reviewDays: number = 0

  @Field({ nullable: true })
  stopDownloadDate?: Date

  @Field(type => Boolean, { nullable: true })
  neverEndingPrivate?: boolean

  @Field(t => [ArcClaimDto], { nullable: true })
  claims?: ArcClaimDto[]

  @Field()
  isDraft!: boolean

  // The audiobook stores' download codes are sent only to the campaign's owner and to staff. A reader is handed
  // their one code through their claim, so for anyone else these resolve to null on every query that returns a
  // campaign - see ownerOrStaffOnly [sc-28919].
  @Field({ nullable: true, middleware: [ownerOrStaffOnly] })
  usDownloadCodes?: string

  @Field({ nullable: true, middleware: [ownerOrStaffOnly] })
  ukDownloadCodes?: string

  @Field({ nullable: true, middleware: [ownerOrStaffOnly] })
  authorsDirectDownloadCodes?: string

  @Field({ nullable: true, middleware: [ownerOrStaffOnly] })
  koboDownloadCodes?: string

  @Field(t => Int, { defaultValue: 0 })
  vipExclusiveAccessDays: number = 0

  @Field({ nullable: true })
  dueDate?: Date

  @Field({ nullable: true })
  dueCutOffDate?: Date

  @Field(t => ArcModelStatsDto, { nullable: true })
  stats?: ArcModelStatsDto

  @Field(type => Int, { nullable: true })
  importedId?: number

  @Field(t => Int)
  alertFlags!: number

  @Field({ nullable: true })
  liveDateStr?: string

  @Field({ nullable: true })
  stopDownloadDateStr?: string

  @Field({ nullable: true })
  dueDateStr?: string

  @Field({ nullable: true })
  dueCutOffDateStr?: string

  @Field(t => Int, { nullable: true })
  viewCount?: number

  @Field(t => Int, { nullable: true })
  impressionCount?: number

  /**
   * When the author sent this campaign's one-per-campaign "book file updated" notification, or undefined
   * if they never did. Exposed so the author's arc view can show a "Book file updated" alert only once it
   * has actually gone out - it is also the flag that stops the popup being offered a second time. [sc-28487]
   */
  @Field({ nullable: true })
  fileUpdateNotificationSentDate?: Date

  // Staff-only breakdown of the Recommended score, filled in on an admin's feed page so staff can see why a book
  // ranks where it does (sc-26481, brought back for sc-28740). staffOnly keeps it from everyone else even if a
  // service fills it in.
  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityScore?: number

  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityEngagementIdx?: number

  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityCatCpi?: number

  @Field(t => Int, { nullable: true, middleware: [staffOnly] })
  qualityClaims?: number

  @Field(t => Int, { nullable: true, middleware: [staffOnly] })
  qualityPageCount?: number

  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityPageFactor?: number

  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityAuthorFactor?: number

  @Field(t => Int, { nullable: true, middleware: [staffOnly] })
  qualityFreshBookArcs?: number

  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityFollowFactor?: number

  @Field(t => Float, { nullable: true, middleware: [staffOnly] })
  qualityCategoryFitFactor?: number

  // Every category the book is in, not only the ones the reader filtered on.
  @Field(t => [BookCategoryFitDto], { nullable: true, middleware: [staffOnly] })
  qualityCategoryFits?: BookCategoryFitDto[]
}
